/**
 * เสียงพูดของตัวละคร — สังเคราะห์สด ไม่มีไฟล์เสียง
 *
 * แนวเดียวกับ Animal Crossing / Undertale คือพูดเป็นเสียงอ้อแอ้ไม่เป็นภาษา
 * ข้อดีคือไม่ต้องอัดเสียง ไม่ต้องเลือกภาษา สุ่มไม่ซ้ำทุกครั้ง และแยกบุคลิก
 * ตัวละคร 8 ตัวได้ด้วยตัวเลขล้วน ๆ
 *
 * หัวใจที่ทำให้ฟังเป็น "เสียงพูด" ไม่ใช่ "เสียงบี๊บ" คือฟอร์แมนต์:
 * เสียงสระของคนเกิดจากช่องปากขยายความถี่สองย่านให้เด่นขึ้นมา (F1 กับ F2)
 * เราจึงเอาคลื่นฟันเลื่อย (แทนเส้นเสียง) ผ่านแบนด์พาสสองตัวขนานกัน
 * แล้วสุ่มสระแต่ละพยางค์ ผลที่ได้จะฟังเหมือนพึมพำเป็นคำ
 */

import { masterBus } from './sfx'

/* ------------------------------------------------------------------ */
/* สระ — คู่ความถี่ฟอร์แมนต์ของเสียงคนจริง                            */
/* ------------------------------------------------------------------ */

/** [F1, F2] หน่วยเฮิรตซ์ อ้างอิงค่าเฉลี่ยเสียงผู้ใหญ่ */
const VOWELS: ReadonlyArray<readonly [number, number]> = [
  [730, 1090], // อา
  [270, 2290], // อี
  [300, 870], //  อู
  [530, 1840], // เอ
  [570, 840], //  โอ
  [660, 1720], // แอ
]

export type Emotion = 'ready' | 'happy' | 'sad' | 'cheer'

/**
 * แนวเสียง — ลองฟังแล้วเลือกเอา เปลี่ยนได้จากคอนโซล milloVoice.style('soft')
 *
 * formant  ฟอร์แมนต์เต็มสูตร Q สูง เหมือนคนพูดจริงที่สุดแต่แหบก้องแบบหุ่นยนต์
 * soft     ฟอร์แมนต์อ่อน ๆ คลื่นสามเหลี่ยม นุ่มกว่ามาก ยังพอฟังเป็นเสียงพูด
 * blip     บี๊บล้วนไม่มีฟอร์แมนต์ สั้นห้วน แนว Undertale
 * bubbly   ไซน์กลม ๆ เสียงดีดขึ้นต้นพยางค์ น่ารักแบบของเล่น
 */
export type VoiceStyle = 'formant' | 'soft' | 'blip' | 'bubbly'

let style: VoiceStyle = 'soft'

export function setVoiceStyle(s: VoiceStyle): void {
  style = s
}

/**
 * ชดเชยเกนแยกตามแนว เพราะแต่ละแบบสูญพลังงานที่ฟิลเตอร์ไม่เท่ากัน
 * (formant Q สูงตัดทิ้งเยอะสุด · blip แทบไม่กรองเลยจึงดังเกินถ้าไม่หรี่)
 * ค่าปรับจนวัดพีคได้เท่ากันทุกแนวที่ ~0.25 เวลาเทียบจะได้ยุติธรรม
 * ไม่งั้นแบบที่ดังกว่าจะฟังดูดีกว่าโดยอัตโนมัติทั้งที่ไม่เกี่ยวกัน
 */
const MAKEUP_BY_STYLE: Record<VoiceStyle, number> = {
  formant: 1.85,
  soft: 1.39,
  blip: 0.375,
  bubbly: 0.59,
}

interface VoiceProfile {
  /** ความถี่พื้นของเสียง ยิ่งสูงยิ่งเป็นตัวเล็ก */
  pitch: number
  /** รูปคลื่นแทนเส้นเสียง sawtooth=นุ่ม square=แหบ */
  wave: OscillatorType
  /** คูณความถี่ฟอร์แมนต์ ตัวเล็กช่องปากสั้น ค่าจึงสูงกว่า 1 */
  formant: number
  /** ความเร็วสั่นเสียง (เฮิรตซ์) */
  vibRate: number
  /** ความลึกสั่นเสียง (เซนต์) */
  vibDepth: number
  /** คูณความยาวพยางค์ ยิ่งมากยิ่งพูดช้า */
  pace: number
}

/** คีย์ตรงกับ CRITTERS ใน src/scene/critters.ts */
export const VOICES: Record<string, VoiceProfile> = {
  // แมว — สดใส เสียงกลางค่อนสูง
  millo: { pitch: 300, wave: 'sawtooth', formant: 1.18, vibRate: 5.5, vibDepth: 22, pace: 1 },
  // หมา — อบอุ่น เสียงต่ำกว่า พูดเต็มคำ
  budgie: { pitch: 210, wave: 'sawtooth', formant: 0.94, vibRate: 4.6, vibDepth: 16, pace: 1.12 },
  // แพนด้า — ต่ำ ช้า กลมใหญ่
  porto: { pitch: 160, wave: 'sawtooth', formant: 0.82, vibRate: 3.8, vibDepth: 14, pace: 1.3 },
  // กระต่าย — สูงแหลม พูดเร็ว
  richie: { pitch: 395, wave: 'square', formant: 1.34, vibRate: 6.8, vibDepth: 30, pace: 0.82 },
  // จิ้งจอก — กลางสูง ลื่น เจ้าเล่ห์
  finn: { pitch: 265, wave: 'sawtooth', formant: 1.1, vibRate: 5.9, vibDepth: 26, pace: 0.94 },
  // กบ — ต่ำ แหบ อ๊บ ๆ
  kevin: { pitch: 145, wave: 'square', formant: 0.88, vibRate: 7.5, vibDepth: 34, pace: 1.05 },
  // เพนกวิน — กลางต่ำ ห้วน
  pete: { pitch: 200, wave: 'square', formant: 0.98, vibRate: 4.2, vibDepth: 12, pace: 0.88 },
  // นกฮูก — ต่ำ ทุ้ม สั่นกว้าง ดูมีอายุ
  warren: { pitch: 175, wave: 'sawtooth', formant: 0.9, vibRate: 3.2, vibDepth: 40, pace: 1.35 },
}

const FALLBACK: VoiceProfile = { pitch: 240, wave: 'sawtooth', formant: 1, vibRate: 5, vibDepth: 20, pace: 1 }

interface EmotionShape {
  /** จำนวนพยางค์ที่เป็นไปได้ */
  syllables: [number, number]
  /** ความยาวพยางค์ฐาน (วินาที) */
  step: number
  /** คูณความถี่พยางค์แรก → พยางค์สุดท้าย (ขึ้น = ถาม/ดีใจ ลง = เศร้า) */
  contour: [number, number]
  /** ความดัง */
  gain: number
  /** ช่วงสุ่มความถี่รายพยางค์ */
  jitter: number
  /** เว้นช่องว่างระหว่างพยางค์ (1 = ติดกัน, >1 = ห้วน) */
  spacing: number
}

/**
 * ค่า gain ปรับจากการวัดพีคจริง ไม่ใช่ตั้งตามความรู้สึก
 * เป้าหมายคือให้ ready ดังพอ ๆ กับเสียงเอฟเฟกต์ (~0.20) เพราะมันคือสัญญาณ
 * "ถึงตาคุณแล้ว" ที่ดังบ่อยที่สุดและพลาดไม่ได้ ส่วน sad ตั้งใจให้เบากว่าเพื่อน
 */
const SHAPES: Record<Emotion, EmotionShape> = {
  // ขานรับตอนถึงตา — สั้น เสียงขึ้นท้ายเหมือนถาม "หืม?"
  ready: { syllables: [1, 2], step: 0.1, contour: [0.95, 1.28], gain: 0.35, jitter: 0.06, spacing: 1.15 },
  // ดีใจ — ยาวขึ้น ไล่เสียงสูง สดใส
  happy: { syllables: [3, 4], step: 0.088, contour: [1, 1.42], gain: 0.24, jitter: 0.11, spacing: 1 },
  // เสียใจ — ช้า เสียงตก เบา
  sad: { syllables: [2, 3], step: 0.16, contour: [1.02, 0.66], gain: 0.19, jitter: 0.05, spacing: 1.25 },
  // เร่งเร้า — ห้วน ดัง กระแทกเป็นจังหวะ
  cheer: { syllables: [3, 3], step: 0.072, contour: [1.15, 1.1], gain: 0.23, jitter: 0.14, spacing: 1.5 },
}

/* ------------------------------------------------------------------ */

const rnd = (a: number, b: number) => a + Math.random() * (b - a)

/**
 * เกนชดเชยเล็งให้พีคใกล้เสียงเอฟเฟกต์ในเกม (~0.20) — วัดคลื่นจริง ไม่ได้ตั้งตามความรู้สึก
 * ถ้าไปแก้ Q หรือโครงสร้างฟิลเตอร์เมื่อไหร่ ต้องวัดใหม่ ค่าเดิมจะใช้ไม่ได้ทันที
 */

/** กันพูดรัวซ้อนกันเอง เก็บเวลาที่พูดล่าสุดของแต่ละตัว */
const lastSpoke = new Map<string, number>()
const COOLDOWN_MS = 260

/**
 * ให้ตัวละครพูดหนึ่งประโยคสั้น ๆ
 * @param character คีย์ตัวละคร (millo, budgie, ...) ถ้าไม่รู้จักจะใช้เสียงกลาง
 * @param emotion   อารมณ์ที่จะพูด
 */
export function speak(character: string, emotion: Emotion): void {
  const bus = masterBus()
  if (!bus) return
  const ctx = bus.context as AudioContext

  const key = `${character}:${emotion}`
  const nowMs = performance.now()
  const prev = lastSpoke.get(character) ?? -Infinity
  if (nowMs - prev < COOLDOWN_MS) return
  lastSpoke.set(character, nowMs)
  void key

  const v = VOICES[character] ?? FALLBACK
  const s = SHAPES[emotion]

  const count = Math.round(rnd(s.syllables[0], s.syllables[1]))
  const step = s.step * v.pace
  const t0 = ctx.currentTime + 0.01

  // รวมทุกพยางค์ไว้ใต้เกนเดียว จะได้คุมความดังรวมและตัดเสียงพร้อมกันได้
  //
  // MAKEUP: แบนด์พาสฟอร์แมนต์ Q สูงตัดพลังงานทิ้งเกือบหมด วัดแล้วเสียงพูดออกมา
  // เบากว่าเสียงเอฟเฟกต์ในเกม 2-6 เท่า ต้องชดเชยกลับ ไม่งั้นเล่นจริงจะไม่ได้ยิน
  const out = ctx.createGain()
  out.gain.value = s.gain * MAKEUP_BY_STYLE[style]
  // ลิมิตเตอร์กันพีคทะลุ เพราะแต่ละตัว/แต่ละอารมณ์ระดับต่างกันมาก
  // (กระต่ายเสียงแหลมดังกว่ากบเสียงต่ำเกินสองเท่าถ้าไม่คุม)
  const limiter = ctx.createDynamicsCompressor()
  limiter.threshold.value = -14
  limiter.knee.value = 6
  limiter.ratio.value = 8
  limiter.attack.value = 0.003
  limiter.release.value = 0.12
  out.connect(limiter).connect(bus)

  for (let i = 0; i < count; i++) {
    const k = count === 1 ? 1 : i / (count - 1)
    const contour = s.contour[0] + (s.contour[1] - s.contour[0]) * k
    const freq = v.pitch * contour * rnd(1 - s.jitter, 1 + s.jitter)
    syllable(ctx, out, v, freq, t0 + i * step * s.spacing, step)
  }

  // ปลดโหนดทิ้งหลังพูดจบ ไม่ให้สะสม
  const total = t0 + count * step * s.spacing + 0.4
  const stopAt = (total - ctx.currentTime) * 1000 + 120
  window.setTimeout(() => {
    out.disconnect()
    limiter.disconnect()
  }, Math.max(200, stopAt))
}

/** ฟังทั้ง 8 ตัวเรียงกันด้วยอารมณ์เดียว */
export function speakAll(emotion: Emotion): void {
  const ids = Object.keys(VOICES)
  ids.forEach((id, i) =>
    window.setTimeout(() => {
      lastSpoke.delete(id)
      speak(id, emotion)
    }, i * 750),
  )
}

/** ฟังแนวเสียงทั้ง 4 แบบเทียบกัน ใช้เลือกว่าจะเอาแบบไหน */
export function compareStyles(character = 'millo', emotion: Emotion = 'happy'): void {
  const styles: VoiceStyle[] = ['formant', 'soft', 'blip', 'bubbly']
  const keep = style
  styles.forEach((s, i) =>
    window.setTimeout(() => {
      style = s
      lastSpoke.delete(character)
      console.log(`[เสียง] ${i + 1}/4 — ${s}`)
      speak(character, emotion)
      if (i === styles.length - 1) window.setTimeout(() => (style = keep), 900)
    }, i * 1300),
  )
}

/**
 * ลองฟังจากคอนโซล
 *   milloVoice.compare()              ฟัง 4 แนวเทียบกัน (เริ่มที่นี่)
 *   milloVoice.style('blip')          เลือกแนวที่ชอบ
 *   milloVoice.all('happy')           ฟังทั้ง 8 ตัว
 *   milloVoice('kevin', 'sad')        เจาะตัวเดียว
 */
;(window as unknown as { milloVoice: unknown }).milloVoice = Object.assign(speak, {
  all: speakAll,
  compare: compareStyles,
  style: setVoiceStyle,
  styles: ['formant', 'soft', 'blip', 'bubbly'],
})

/** ตัวเลขที่ทำให้แต่ละแนวเสียงต่างกัน */
const STYLE_TUNE: Record<
  VoiceStyle,
  { wave: OscillatorType | null; q1: number; q2: number; g2: number; cut: number; vib: number; glide: number }
> = {
  // เดิม: ฟันเลื่อยผ่านแบนด์พาส Q สูง ก้องแบบหุ่นยนต์ สั่นลึก รูดเสียงลงทุกพยางค์
  formant: { wave: null, q1: 7, q2: 11, g2: 0.6, cut: 3400, vib: 1, glide: 0.9 },
  // นุ่มลง: บังคับสามเหลี่ยม ลด Q ลงเยอะ ตัดเสียงสูงลงมา ลดสั่นครึ่งหนึ่ง
  soft: { wave: 'triangle', q1: 2.2, q2: 3, g2: 0.35, cut: 2400, vib: 0.45, glide: 0.96 },
  // บี๊บล้วน ไม่มีฟอร์แมนต์เลย (q ต่ำมากจนแทบไม่กรอง) สั้นห้วน
  blip: { wave: 'square', q1: 0.35, q2: 0.35, g2: 0, cut: 1900, vib: 0.25, glide: 1 },
  // กลมมน ไซน์ล้วน สั่นเบา ๆ
  bubbly: { wave: 'sine', q1: 0.4, q2: 0.4, g2: 0, cut: 2600, vib: 0.6, glide: 1.06 },
}

/** หนึ่งพยางค์: เส้นเสียง → ฟอร์แมนต์ → ออก (รายละเอียดเปลี่ยนตามแนวเสียง) */
function syllable(
  ctx: AudioContext,
  dest: GainNode,
  v: VoiceProfile,
  freq: number,
  at: number,
  dur: number,
): void {
  const [f1, f2] = VOWELS[Math.floor(Math.random() * VOWELS.length)]
  const st = STYLE_TUNE[style]

  const osc = ctx.createOscillator()
  osc.type = st.wave ?? v.wave
  osc.frequency.setValueAtTime(freq, at)
  // ไล่เสียงในพยางค์ — ลงเล็กน้อยฟังเป็นคำพูด ขึ้นเล็กน้อยฟังน่ารักแบบของเล่น
  osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq * st.glide), at + dur)

  // สั่นเสียง — คนพูดไม่มีใครเสียงนิ่งสนิท แต่ลึกเกินจะเมาเรือ
  const lfo = ctx.createOscillator()
  const lfoGain = ctx.createGain()
  lfo.frequency.value = v.vibRate
  lfoGain.gain.value = v.vibDepth * st.vib
  lfo.connect(lfoGain).connect(osc.detune)

  // ซองเสียงของพยางค์: ขึ้นเร็ว ลงเร็ว
  const env = ctx.createGain()
  env.gain.setValueAtTime(0.0001, at)
  env.gain.exponentialRampToValueAtTime(1, at + dur * 0.18)
  env.gain.exponentialRampToValueAtTime(0.0001, at + dur * 0.92)

  // ฟอร์แมนต์ F1 กับ F2 ขนานกัน คือตัวที่ทำให้กลายเป็นเสียงสระ
  // Q ยิ่งสูงยิ่งเหมือนเสียงคน แต่ก็ยิ่งก้องจนบาดหู
  const b1 = ctx.createBiquadFilter()
  b1.type = 'bandpass'
  b1.frequency.value = f1 * v.formant
  b1.Q.value = st.q1
  const g1 = ctx.createGain()
  g1.gain.value = 1

  const b2 = ctx.createBiquadFilter()
  b2.type = 'bandpass'
  b2.frequency.value = f2 * v.formant
  b2.Q.value = st.q2
  const g2 = ctx.createGain()
  g2.gain.value = st.g2

  // ตัดความถี่สูงที่บาดหูออก ให้ฟังนุ่มแบบการ์ตูน
  const soft = ctx.createBiquadFilter()
  soft.type = 'lowpass'
  soft.frequency.value = st.cut

  osc.connect(env)
  env.connect(b1).connect(g1).connect(soft)
  if (st.g2 > 0) env.connect(b2).connect(g2).connect(soft)
  soft.connect(dest)

  const end = at + dur + 0.06
  osc.start(at)
  osc.stop(end)
  lfo.start(at)
  lfo.stop(end)
  osc.onended = () => {
    osc.disconnect()
    lfo.disconnect()
    lfoGain.disconnect()
    env.disconnect()
    b1.disconnect()
    b2.disconnect()
    g1.disconnect()
    g2.disconnect()
    soft.disconnect()
  }
}
